<template>
  <div
    v-if="players.length === 0"
    class="rounded-lg border border-dashed border-border px-4 py-8 text-center text-sm text-text-muted"
  >
    {{ emptyMessage }}
  </div>
  <div
    v-else
    class="grid grid-cols-1 gap-2 xl:grid-cols-2"
    role="list"
    :aria-label="listLabel"
  >
    <component
      :is="isSelectable ? 'button' : 'article'"
      v-for="player in players"
      :key="player.id"
      :type="isSelectable ? 'button' : undefined"
      :role="isSelectable ? 'checkbox' : 'listitem'"
      :class="rowClass(player)"
      :aria-checked="isSelectable ? String(isPlayerSelected(player)) : undefined"
      :aria-busy="isPlayerPending(player) ? 'true' : undefined"
      :aria-label="isSelectable ? playerToggleLabel(player) : undefined"
      :title="rowTitle(player)"
      :disabled="isSelectable ? !canTogglePlayer(player) : undefined"
      @click="togglePlayer(player)"
    >
      <span
        v-if="isSelectable"
        class="mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded border"
        :class="isPlayerSelected(player) ? 'border-accent bg-accent text-bg' : 'border-border-strong bg-bg'"
        aria-hidden="true"
      >
        <AppIcon
          v-if="isPlayerSelected(player)"
          :name="IconName.Check"
          :size="14"
          :stroke-width="2.4"
        />
      </span>

      <span class="flex min-w-0 flex-1 flex-col gap-2">
        <span class="flex min-w-0 flex-wrap items-start justify-between gap-x-3 gap-y-1">
          <span class="min-w-0">
            <span class="block truncate text-sm font-semibold text-text-primary">
              {{ displayPlayerName(player) }}
            </span>
            <span v-if="player.inGameName !== ''" class="block truncate text-xs text-text-muted">
              {{ player.discordName }}
            </span>
          </span>
          <span class="flex shrink-0 flex-wrap justify-end gap-1">
            <span
              v-for="label in sourceLabels(player)"
              :key="label"
              class="rounded-full border border-accent/40 bg-accent/10 px-2 py-0.5 font-mono text-2xs uppercase tracking-wide text-accent"
            >
              {{ label }}
            </span>
            <span class="rounded-full border border-border px-2 py-0.5 font-mono text-2xs uppercase tracking-wide text-text-muted">
              {{ selectionStatusLabel(player) }}
            </span>
          </span>
        </span>

        <span
          v-if="isGroupSelector && rosterBuildChoices(player).length > 0"
          class="flex min-w-0 flex-col gap-1.5"
        >
          <span
            v-for="choice in rosterBuildChoices(player)"
            :key="choice.key"
            class="flex min-w-0 flex-wrap items-center gap-1.5 rounded-md border px-2 py-1"
            :class="preferredBuildChoice(player)?.key === choice.key ? 'border-accent/60 bg-accent/10' : 'border-border bg-bg'"
          >
            <span class="min-w-20 font-mono text-2xs uppercase tracking-wide text-text-muted">
              {{ buildChoiceLabel(choice) }}
            </span>
            <span
              v-for="slug in choice.weaponSlugs"
              :key="`${choice.key}-${slug}`"
              class="inline-flex min-w-0 items-center gap-1 text-2xs text-text-secondary"
              :title="weaponName(slug)"
            >
              <img
                :src="weaponIcon(slug)"
                alt=""
                class="size-6 shrink-0 rounded-sm object-cover"
                aria-hidden="true"
              />
              <span class="max-w-28 truncate">{{ weaponName(slug) }}</span>
            </span>
            <span
              v-if="preferredBuildChoice(player)?.key === choice.key"
              class="ml-auto rounded-full border border-accent/40 px-2 py-0.5 font-mono text-2xs uppercase tracking-wide text-accent"
              :title="messages.resultList.willUseChoiceHint"
            >
              {{ messages.resultList.preferredBuildBadge }}
            </span>
          </span>
        </span>

        <span v-else class="flex min-w-0 flex-wrap items-center gap-1.5">
          <span
            v-for="slug in displayedWeaponSlugs(player)"
            :key="slug"
            class="inline-flex min-w-0 items-center gap-1 rounded-md border border-border bg-bg px-1.5 py-1 text-2xs text-text-secondary"
            :title="weaponName(slug)"
          >
            <img
              :src="weaponIcon(slug)"
              alt=""
              class="size-6 shrink-0 rounded-sm object-cover"
              aria-hidden="true"
            />
            <span class="max-w-32 truncate">{{ weaponName(slug) }}</span>
          </span>
          <span v-if="displayedWeaponSlugs(player).length === 0" class="text-xs text-text-muted">
            {{ messages.resultList.noWeapons }}
          </span>
        </span>

        <span v-if="player.tags.length > 0" class="flex min-w-0 flex-wrap items-center gap-1">
          <span
            v-for="tag in visibleTags(player)"
            :key="tag.id"
            class="inline-flex min-h-6 items-center rounded-md px-2 text-2xs font-semibold"
            :class="tagChipClassFor({ color: tag.color, active: false })"
          >
            {{ tagDisplayName(tag.name) }}
          </span>
          <span
            v-if="player.tags.length > visibleTags(player).length"
            class="text-2xs font-semibold text-text-muted"
          >
            {{ messages.resultList.overflowTags(player.tags.length - visibleTags(player).length) }}
          </span>
        </span>
      </span>
    </component>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { WEAPONS_BY_SLUG, weaponIcon } from '@/data/weapons'
import { tagChipClassFor, tagDisplayName } from '@/features/gvg-planner/composables'
import { AppIcon, IconName } from '@/shared/ui'
import { defineLocaleCatalog } from '@/shared/i18n'
import { useLocaleMessages } from '@/shared/composables/useI18n'
import {
  POOL_OTHER_GROUP_ID,
  POOL_SIGNED_GROUP_ID,
} from '../../lib/boardInvariants'
import {
  firstPlayerBuildChoice,
  playerBuildChoiceMatchesWeapons,
  playerBuildChoices,
  playerCurrentWeaponSlugs,
  playerSavedWeaponMatch,
  PlayerBuildSource,
  raidBuildIsSelected,
} from '../../lib/playerWeaponBuilds'
import type { PlayerBuildChoice } from '../../lib/playerWeaponBuilds'
import { playerDisplayName } from '../../lib/playerIdentity'
import { useGvgPlannerStore } from '../../stores/gvgPlannerStore'
import type { Player, PlayerGroup, PlayerTag } from '../../types'
import { playerFiltersEn } from './locales/en.ts'
import { playerFiltersRu } from './locales/ru.ts'

const MAX_VISIBLE_TAGS = 3

export interface PlayerFilterResultListProps {
  readonly players: ReadonlyArray<Player>
  readonly source: 'current' | 'saved'
  readonly listLabel: string
  readonly emptyMessage: string
  readonly targetGroupId?: string | null
  readonly targetGroupLabel?: string
  readonly targetTagId?: string | null
  readonly targetTagLabel?: string
  readonly pendingTargets: ReadonlyMap<string, string>
  readonly pendingTagPlayerIds?: ReadonlySet<string>
  readonly weaponFilterSlugs?: ReadonlyArray<string>
}

const props = withDefaults(defineProps<PlayerFilterResultListProps>(), {
  targetGroupId: null,
  // The previous literal `'group'` / `'tag'` defaults leaked English
  // into a localized prop contract. The localized fallbacks are
  // resolved through `messages` below (see `computed`
  // `groupLabelFallback` / `tagLabelFallback`). The prop accessors
  // return those computed values transparently at render time.
  targetGroupLabel: '',
  targetTagId: null,
  targetTagLabel: '',
  pendingTagPlayerIds: () => new Set<string>(),
  weaponFilterSlugs: () => [],
})

const emit = defineEmits<{
  (
    event: 'toggle-player',
    playerId: string,
    selected: boolean,
    buildChoice: PlayerBuildChoice | null,
  ): void
}>()

const messages = useLocaleMessages(defineLocaleCatalog({ en: playerFiltersEn, ru: playerFiltersRu }))
const store = useGvgPlannerStore()
const isGroupSelector = computed<boolean>(() => props.targetGroupId !== null)
const isTagSelector = computed<boolean>(() => props.targetTagId !== null)
const isSelectable = computed<boolean>(() => isGroupSelector.value || isTagSelector.value)

// Localized fallbacks for the optional `targetGroupLabel` /
// `targetTagLabel` props. Vue's `<script setup>` compiler hoists
// `defineProps` to the top of the generated setup function, so the
// defaulted values cannot reference `messages` directly. We resolve
// the fallbacks through these `computed` getters and read them from
// the prop accessors in `playerToggleLabel`.
const groupLabelFallback = computed<string>(
  () => messages.value.resultList.fallbackGroupLabelProp,
)
const tagLabelFallback = computed<string>(
  () => messages.value.resultList.fallbackTagLabelProp,
)
const resolvedTargetGroupLabel = computed<string>(
  () => props.targetGroupLabel !== '' && props.targetGroupLabel !== undefined
    ? props.targetGroupLabel
    : groupLabelFallback.value,
)
const resolvedTargetTagLabel = computed<string>(
  () => props.targetTagLabel !== '' && props.targetTagLabel !== undefined
    ? props.targetTagLabel
    : tagLabelFallback.value,
)

function displayGroupLabel(group: PlayerGroup): string {
  if (group.label !== '') return group.label
  const room = store.currentRooms.find((candidate) => candidate.groupIds.includes(group.id))
  const index = room?.groupIds.indexOf(group.id) ?? -1
  return index < 0 ? group.id : messages.value.resultList.fallbackGroupLabel(index + 1)
}

function displayedWeaponSlugs(player: Player): ReadonlyArray<string> {
  return props.source === 'current'
    ? playerCurrentWeaponSlugs(player)
    : playerSavedWeaponMatch(player, props.weaponFilterSlugs).weaponSlugs
}

function rosterBuildChoices(player: Player): ReadonlyArray<PlayerBuildChoice> {
  const choices = playerBuildChoices(player, props.weaponFilterSlugs)
  if (props.source === 'current' || props.weaponFilterSlugs.length === 0) return choices
  return choices.filter((choice) =>
    playerBuildChoiceMatchesWeapons(choice, props.weaponFilterSlugs),
  )
}

function preferredBuildChoice(player: Player): PlayerBuildChoice | null {
  if (store.activeRaidId === null) return null
  const raidActiveBuildIndex = store.raidPlayers
    .find((candidate) => candidate.id === player.id)
    ?.activeBuildIndex
  if (raidBuildIsSelected(raidActiveBuildIndex)) return null
  if (props.source === 'saved') return rosterBuildChoices(player)[0] ?? null
  return firstPlayerBuildChoice(player, props.weaponFilterSlugs)
}

function buildChoiceLabel(choice: PlayerBuildChoice): string {
  if (choice.source === PlayerBuildSource.Guild) {
    return messages.value.resultList.buildChoiceGuild(choice.sourceIndex)
  }
  if (choice.sourceIndex === 0) {
    return messages.value.resultList.buildChoicePlayerDefault
  }
  return messages.value.resultList.buildChoicePlayer(choice.sourceIndex)
}

function sourceLabels(player: Player): ReadonlyArray<string> {
  if (props.source === 'current') return []
  const match = playerSavedWeaponMatch(player, props.weaponFilterSlugs)
  return [
    ...(match.personal ? [messages.value.resultList.sourceLabelPlayerBuild] : []),
    ...(match.guild ? [messages.value.resultList.sourceLabelGuildBuild] : []),
  ]
}

function weaponName(slug: string): string {
  return WEAPONS_BY_SLUG.get(slug)?.name ?? slug
}

function visibleTags(player: Player): ReadonlyArray<PlayerTag> {
  return player.tags.slice(0, MAX_VISIBLE_TAGS)
}

function effectiveGroupId(player: Player): string | null {
  return props.pendingTargets.get(player.id) ?? player.group
}

function isPlayerSelected(player: Player): boolean {
  if (props.targetTagId !== null) return player.tags.some((tag) => tag.id === props.targetTagId)
  return props.targetGroupId !== null && effectiveGroupId(player) === props.targetGroupId
}

function isPlayerPending(player: Player): boolean {
  return isTagSelector.value
    ? props.pendingTagPlayerIds.has(player.id)
    : props.pendingTargets.has(player.id)
}

function fallbackGroupId(player: Player): string {
  return player.hasSignedIn ? POOL_SIGNED_GROUP_ID : POOL_OTHER_GROUP_ID
}

function canTogglePlayer(player: Player): boolean {
  if (!isSelectable.value || isPlayerPending(player)) return false
  if (isTagSelector.value) return true
  return !isPlayerSelected(player) || fallbackGroupId(player) !== props.targetGroupId
}

function displayPlayerName(player: Player): string {
  return playerDisplayName(player, store.playerNameMode)
}

function playerToggleLabel(player: Player): string {
  const name = displayPlayerName(player)
  if (isTagSelector.value) {
    return isPlayerSelected(player)
      ? messages.value.resultList.playerToggleAriaRemoveTag(resolvedTargetTagLabel.value, name)
      : messages.value.resultList.playerToggleAriaAssignTag(resolvedTargetTagLabel.value, name)
  }
  return isPlayerSelected(player)
    ? messages.value.resultList.playerToggleAriaRemoveFromGroup(name, resolvedTargetGroupLabel.value)
    : messages.value.resultList.playerToggleAriaMoveToGroup(name, resolvedTargetGroupLabel.value)
}

function playerToggleTitle(player: Player): string {
  if (isPlayerPending(player)) return messages.value.resultList.playerToggleTitlePending
  if (isPlayerSelected(player) && fallbackGroupId(player) === props.targetGroupId) {
    return player.hasSignedIn
      ? messages.value.resultList.playerToggleTitleFallbackSigned
      : messages.value.resultList.playerToggleTitleFallbackOther
  }
  return playerToggleLabel(player)
}

function rowTitle(player: Player): string {
  if (isSelectable.value) return playerToggleTitle(player)
  const labels = sourceLabels(player)
  return labels.length === 0
    ? messages.value.resultList.rowTitleCurrent(displayPlayerName(player))
    : messages.value.resultList.rowTitleLabels(displayPlayerName(player), labels.join(' and '))
}

function groupLabelFor(groupId: string | null): string {
  if (groupId === null) return messages.value.resultList.fallbackGroupUnassigned
  const group = store.currentGroups.find((candidate) => candidate.id === groupId)
  if (group === undefined) return groupId
  const room = store.currentRooms.find((candidate) => candidate.groupIds.includes(groupId))
  const groupLabel = displayGroupLabel(group)
  return room === undefined ? groupLabel : messages.value.resultList.groupLabelWithRoom(room.name, groupLabel)
}

function selectionStatusLabel(player: Player): string {
  if (isPlayerPending(player)) {
    return isTagSelector.value ? messages.value.resultList.pendingStatusTag : messages.value.resultList.pendingStatusMove
  }
  return groupLabelFor(effectiveGroupId(player))
}

function rowClass(player: Player): string {
  const base = 'flex min-w-0 items-start gap-3 rounded-lg border p-3 text-left'
  if (!isSelectable.value) return `${base} border-border bg-bg-surface`
  const interaction = 'transition-[border-color,background-color,opacity] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:cursor-not-allowed disabled:opacity-60'
  const state = isPlayerSelected(player)
    ? 'border-accent bg-accent/10'
    : 'border-border bg-bg-surface hover:border-border-strong hover:bg-bg-elevated'
  return `${base} ${interaction} ${state}`
}

function togglePlayer(player: Player): void {
  if (!canTogglePlayer(player)) return
  const selected = !isPlayerSelected(player)
  emit(
    'toggle-player',
    player.id,
    selected,
    selected && isGroupSelector.value ? preferredBuildChoice(player) : null,
  )
}
</script>
